import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { ChevronRight, Home, ArrowLeft, Star, Clock, Search, Command } from 'lucide-react';

// Navigation context types
interface NavigationState {
  currentView: 'editor' | 'dashboard' | 'agent-dashboard' | 'test-manager' | 'results';
  previousView?: string;
  breadcrumbs: BreadcrumbItem[];
  context: Record<string, any>;
  favorites: FavoriteItem[];
  recentItems: RecentItem[];
  sessionData: Record<string, any>;
}

interface BreadcrumbItem {
  id: string;
  label: string;
  path: string;
  context?: Record<string, any>;
}

interface FavoriteItem {
  id: string;
  type: 'template' | 'test' | 'agent' | 'campaign';
  name: string;
  path: string;
  context: Record<string, any>;
  addedAt: string;
}

interface RecentItem {
  id: string;
  type: 'template' | 'test' | 'agent' | 'campaign';
  name: string;
  path: string;
  context: Record<string, any>;
  accessedAt: string;
}

interface NavigationContextType {
  state: NavigationState;
  navigateTo: (view: string, context?: Record<string, any>) => void;
  goBack: () => void;
  addBreadcrumb: (item: BreadcrumbItem) => void;
  clearBreadcrumbs: () => void;
  addToFavorites: (item: Omit<FavoriteItem, 'id' | 'addedAt'>) => void;
  removeFromFavorites: (id: string) => void;
  addToRecent: (item: Omit<RecentItem, 'id' | 'accessedAt'>) => void;
  preserveSession: (key: string, data: any) => void;
  getSessionData: (key: string) => any;
  clearSession: () => void;
  resetNavigation: () => void;
}

// Create navigation context
const NavigationContext = createContext<NavigationContextType | null>(null);

// Navigation provider component
export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<NavigationState>(() => {
    // Load from localStorage if available
    const saved = localStorage.getItem('abtest-navigation-state');
    if (saved) {
      try {
        const parsedState = JSON.parse(saved);
        // Clean up breadcrumbs to prevent multiple tabs issue
        const cleanBreadcrumbs = parsedState.breadcrumbs?.filter((crumb: BreadcrumbItem, index: number, self: BreadcrumbItem[]) => 
          index === self.findIndex(c => c.path === crumb.path)
        ).slice(0, 3) || [{ id: 'home', label: 'A/B Testing', path: '/ab-testing' }];
        
        return {
          ...parsedState,
          breadcrumbs: cleanBreadcrumbs
        };
      } catch (e) {
        console.warn('Failed to parse saved navigation state');
      }
    }
    
    return {
      currentView: 'dashboard',
      breadcrumbs: [{ id: 'home', label: 'A/B Testing', path: '/ab-testing' }],
      context: {},
      favorites: [],
      recentItems: [],
      sessionData: {}
    };
  });

  // Save state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('abtest-navigation-state', JSON.stringify(state));
  }, [state]);

  const navigateTo = useCallback((view: string, context: Record<string, any> = {}) => {
    setState(prev => {
      // Don't add duplicate breadcrumbs for the same view or similar paths
      const currentBreadcrumb = prev.breadcrumbs[prev.breadcrumbs.length - 1];
      let newBreadcrumbs = [...prev.breadcrumbs];
      
      // Check if we're navigating to the same view or a similar one
      const isSameView = currentBreadcrumb?.path === view;
      const isSimilarView = currentBreadcrumb?.path?.includes(view) || view.includes(currentBreadcrumb?.path || '');
      
      // Only add breadcrumb if it's genuinely different
      if (!isSameView && !isSimilarView) {
        // Remove duplicates and limit breadcrumbs to maximum of 3 items (including home)
        newBreadcrumbs = newBreadcrumbs.filter((crumb, index, self) => 
          index === self.findIndex(c => c.path === crumb.path)
        );
        
        if (newBreadcrumbs.length >= 3) {
          newBreadcrumbs.splice(1, 1); // Remove second item, keep home and most recent
        }
        
        // Add new breadcrumb
        const viewLabels: Record<string, string> = {
          'dashboard': 'Dashboard',
          'agent-dashboard': 'AI Agents',
          'test-manager': 'Test Manager',
          'editor': 'Template Editor',
          'results': 'Results'
        };
        
        newBreadcrumbs.push({
          id: `breadcrumb-${Date.now()}`,
          label: viewLabels[view] || view,
          path: view,
          context
        });
      }
      
      return {
        ...prev,
        previousView: prev.currentView,
        currentView: view as any,
        context: { ...prev.context, ...context },
        breadcrumbs: newBreadcrumbs
      };
    });
  }, []);

  const goBack = useCallback(() => {
    setState(prev => ({
      ...prev,
      currentView: prev.previousView as any || 'dashboard',
      previousView: undefined
    }));
  }, []);

  const addBreadcrumb = useCallback((item: BreadcrumbItem) => {
    setState(prev => ({
      ...prev,
      breadcrumbs: [...prev.breadcrumbs, item]
    }));
  }, []);

  const clearBreadcrumbs = useCallback(() => {
    setState(prev => ({
      ...prev,
      breadcrumbs: [{ id: 'home', label: 'A/B Testing', path: '/ab-testing' }]
    }));
  }, []);

  const addToFavorites = useCallback((item: Omit<FavoriteItem, 'id' | 'addedAt'>) => {
    const favoriteItem: FavoriteItem = {
      ...item,
      id: `fav-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      addedAt: new Date().toISOString()
    };

    setState(prev => ({
      ...prev,
      favorites: [favoriteItem, ...prev.favorites.slice(0, 19)] // Keep max 20 favorites
    }));
  }, []);

  const removeFromFavorites = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      favorites: prev.favorites.filter(fav => fav.id !== id)
    }));
  }, []);

  const addToRecent = useCallback((item: Omit<RecentItem, 'id' | 'accessedAt'>) => {
    const recentItem: RecentItem = {
      ...item,
      id: `recent-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      accessedAt: new Date().toISOString()
    };

    setState(prev => ({
      ...prev,
      recentItems: [recentItem, ...prev.recentItems.filter(r => r.path !== item.path).slice(0, 9)] // Keep max 10 recent items
    }));
  }, []);

  const preserveSession = useCallback((key: string, data: any) => {
    setState(prev => ({
      ...prev,
      sessionData: { ...prev.sessionData, [key]: data }
    }));
  }, []);

  const getSessionData = useCallback((key: string) => {
    return state.sessionData[key];
  }, [state.sessionData]);

  const clearSession = useCallback(() => {
    setState(prev => ({
      ...prev,
      sessionData: {}
    }));
  }, []);

  const resetNavigation = useCallback(() => {
    // Clear localStorage and reset to default state
    localStorage.removeItem('abtest-navigation-state');
    setState({
      currentView: 'dashboard',
      breadcrumbs: [{ id: 'home', label: 'A/B Testing', path: '/ab-testing' }],
      context: {},
      favorites: [],
      recentItems: [],
      sessionData: {}
    });
  }, []);

  const contextValue: NavigationContextType = {
    state,
    navigateTo,
    goBack,
    addBreadcrumb,
    clearBreadcrumbs,
    addToFavorites,
    removeFromFavorites,
    addToRecent,
    preserveSession,
    getSessionData,
    clearSession,
    resetNavigation
  };

  return (
    <NavigationContext.Provider value={contextValue}>
      {children}
    </NavigationContext.Provider>
  );
};

// Hook to use navigation context
export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

// Breadcrumb navigation component
export const BreadcrumbNavigation: React.FC = () => {
  const { state, navigateTo } = useNavigation();

  return (
    <nav className="flex items-center space-x-2 text-sm text-content-secondary mb-4" aria-label="Breadcrumb">
      <Home className="w-4 h-4" />
      {state.breadcrumbs.map((crumb, index) => (
        <React.Fragment key={crumb.id}>
          {index > 0 && <ChevronRight className="w-4 h-4 text-content-subtle" />}
          <button
            onClick={() => navigateTo(crumb.path, crumb.context)}
            className={`hover:text-blue-600 dark:hover:text-blue-300 transition-colors ${
              index === state.breadcrumbs.length - 1 
                ? 'text-content font-medium cursor-default' 
                : 'text-blue-600 dark:text-blue-300 hover:underline'
            }`}
            disabled={index === state.breadcrumbs.length - 1}
          >
            {crumb.label}
          </button>
        </React.Fragment>
      ))}
    </nav>
  );
};

// Quick switcher component (Cmd/Ctrl+K)
export const QuickSwitcher: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const { state, navigateTo, addToRecent } = useNavigation();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);

  const allItems = [
    ...state.favorites.map(fav => ({ ...fav, category: 'Favorites' })),
    ...state.recentItems.map(recent => ({ ...recent, category: 'Recent' })),
    // Add static navigation items
    { id: 'dashboard', name: 'A/B Testing Dashboard', path: 'dashboard', type: 'navigation', category: 'Navigation', context: {} },
    { id: 'editor', name: 'Template Editor', path: 'editor', type: 'navigation', category: 'Navigation', context: {} },
    { id: 'agents', name: 'AI Agent Dashboard', path: 'agent-dashboard', type: 'navigation', category: 'Navigation', context: {} },
    { id: 'test-manager', name: 'Test Manager', path: 'test-manager', type: 'navigation', category: 'Navigation', context: {} }
  ];

  const filteredItems = allItems.filter(item =>
    item.name.toLowerCase().includes(query.toLowerCase()) ||
    item.type.toLowerCase().includes(query.toLowerCase())
  );

  const handleSelect = (item: any) => {
    navigateTo(item.path, item.context);
    addToRecent({
      type: item.type as any,
      name: item.name,
      path: item.path,
      context: item.context
    });
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, filteredItems.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredItems[selectedIndex]) {
          handleSelect(filteredItems[selectedIndex]);
        }
        break;
      case 'Escape':
        onClose();
        break;
    }
  };

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center pt-20 z-50">
      <div className="bg-surface rounded-lg shadow-2xl w-full max-w-2xl mx-4">
        <div className="p-4 border-b border-line">
          <div className="flex items-center space-x-3">
            <Search className="w-5 h-5 text-content-subtle" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search templates, tests, agents..."
              className="flex-1 outline-none text-lg"
              autoFocus
            />
            <div className="flex items-center space-x-1 text-xs text-content-subtle">
              <Command className="w-3 h-3" />
              <span>K</span>
            </div>
          </div>
        </div>

        <div className="max-h-96 overflow-y-auto">
          {filteredItems.length === 0 ? (
            <div className="p-8 text-center text-content-muted">
              <Search className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p>No results found</p>
            </div>
          ) : (
            <div className="py-2">
              {Object.entries(
                filteredItems.reduce((acc, item, index) => {
                  const category = item.category || 'Other';
                  if (!acc[category]) acc[category] = [];
                  acc[category].push({ ...item, originalIndex: index });
                  return acc;
                }, {} as Record<string, any[]>)
              ).map(([category, items]) => (
                <div key={category}>
                  <div className="px-4 py-2 text-xs font-medium text-content-muted uppercase tracking-wide">
                    {category}
                  </div>
                  {items.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleSelect(item)}
                      className={`w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center space-x-3 ${
                        selectedIndex === item.originalIndex ? 'bg-blue-50 dark:bg-blue-900/30 border-r-2 border-blue-500' : ''
                      }`}
                    >
                      <div className="flex-shrink-0">
                        {item.type === 'template' && <div className="w-2 h-2 bg-green-500 rounded-full" />}
                        {item.type === 'test' && <div className="w-2 h-2 bg-blue-500 rounded-full" />}
                        {item.type === 'agent' && <div className="w-2 h-2 bg-purple-500 rounded-full" />}
                        {item.type === 'navigation' && <div className="w-2 h-2 bg-gray-500 rounded-full" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-content truncate">{item.name}</div>
                        <div className="text-sm text-content-muted capitalize">{item.type}</div>
                      </div>
                      {item.category === 'Favorites' && <Star className="w-4 h-4 text-yellow-500 dark:text-yellow-400" />}
                      {item.category === 'Recent' && <Clock className="w-4 h-4 text-content-subtle" />}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-3 border-t border-line text-xs text-content-muted flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span>↑↓ Navigate</span>
            <span>↵ Select</span>
            <span>Esc Close</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Back button component
export const BackButton: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { state, goBack } = useNavigation();

  if (!state.previousView) return null;

  return (
    <button
      onClick={goBack}
      className={`flex items-center space-x-2 text-content-secondary hover:text-content transition-colors ${className}`}
    >
      <ArrowLeft className="w-4 h-4" />
      <span>Back</span>
    </button>
  );
};